<script lang="ts">
  import { focus } from "./actions/focus";
  import DialogBase from "./DialogBase.svelte";
  import { createWebdavConfig } from "./fs";
  import { makeFileSystemWebdav, testWebdavConnection } from "./fs-webdav";
  import { captureEvent, telegramNotify } from "./http";
  import { showMessage } from "./message";

  interface Props {
    open?: boolean;
    mountfs: (fs: any) => void;
  }

  let { open = $bindable(false), mountfs }: Props = $props();

  let config = $state(createWebdavConfig());
  let testingConnection = $state(false);
  let errorMessage = $state("");

  let canConnect = $derived(config.url.trim() != "" && !testingConnection);

  function webdavHost(): string {
    try {
      return new URL(config.url.trim()).host;
    } catch {
      return config.url.trim();
    }
  }

  async function onConnect() {
    testingConnection = true;
    errorMessage = "";
    let removeMsg = showMessage("testing webdav connection");
    try {
      config.url = config.url.trim();
      config.user = config.user.trim();
      config.name = config.name.trim();
      await testWebdavConnection(config);
      if (config.name == "") {
        config.name = webdavHost();
      }
      let fs = makeFileSystemWebdav(config);
      let who = config.name;
      telegramNotify(`New WebDAV connection for ${who}`);
      captureEvent("webdav-login", { host: webdavHost() });
      removeMsg();
      mountfs(fs);
      open = false;
    } catch (e) {
      console.error(e);
      errorMessage = (e as Error).toString();
      removeMsg();
    }
    testingConnection = false;
  }

  function close() {
    open = false;
  }
</script>

<DialogBase bind:open title="Connect WebDAV">
  {#snippet main()}
    <div class="flex flex-col text-sm">
      <label for="input-webdav-name">Name: <span class="text-gray-400">(optional)</span></label>
      <input
        use:focus
        type="text"
        id="input-webdav-name"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.name}
      />

      <label class="mt-3" for="input-webdav-url">URL:</label>
      <input
        type="text"
        id="input-webdav-url"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        placeholder="https://cloud.example.com/remote.php/dav/files/user/"
        bind:value={config.url}
      />

      <label class="mt-3" for="input-webdav-user">User:</label>
      <input
        type="text"
        id="input-webdav-user"
        autocomplete="username"
        class="outline-hidden border px-2 py-1"
        bind:value={config.user}
      />

      <label class="mt-3" for="input-webdav-password">Password:</label>
      <input
        type="password"
        id="input-webdav-password"
        autocomplete="current-password"
        class="outline-hidden border px-2 py-1"
        bind:value={config.password}
      />

      {#if errorMessage}
        <div class="mt-2 mb-2 w-72 font-medium break-all text-red-500">
          {errorMessage}
        </div>
      {/if}
    </div>
  {/snippet}

  {#snippet bottom()}
    <div class="flex flex-row justify-between">
      <button onclick={close} class="dlg-btn">Cancel</button>
      <button disabled={!canConnect} onclick={onConnect} class="dlg-btn">Connect WebDAV</button>
    </div>
  {/snippet}
</DialogBase>
